import { useCallback, useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

const POLLING_INTERVAL_MS = 60 * 1000;

// How long after mount the initial page-load phase lasts. Any update
// *detected* (updatefound / already-waiting) within this window is
// classified as a fresh-open update. Unlike the old approach this bounds
// detection time (a quick SW-script fetch + byte compare, typically <2s),
// not install/precache time (which can exceed 10s on large bundles).
const SESSION_WINDOW_MS = 10_000;

/**
 * Service worker update hook for the customer frontend.
 *
 * Uses `useRegisterSW` from `virtual:pwa-register/react` with
 * `registerType: 'prompt'`. The new SW installs and enters the "waiting"
 * state — it does NOT auto-activate. `needsRefresh` becomes true at that
 * point. The SW only takes over when the user confirms via `applyUpdate()`,
 * which calls `updateServiceWorker(true)`. That sends SKIP_WAITING to the
 * waiting SW; once it activates, `controllerchange` fires and the page
 * reloads onto the new bundle.
 *
 * Public API:
 * - `needsRefresh`        — true once a new SW is installed and waiting.
 * - `isMidSessionUpdate`  — true when the update was *detected* after the
 *                           initial page-load window, i.e. while the user
 *                           was already actively using the tab.
 * - `checkForUpdate`      — imperatively polls the SW registration. Called by
 *                           <VersionUpdateBanner /> on visibility, focus, and
 *                           route changes so long-lived tabs detect deploys
 *                           without relying solely on the polling interval.
 * - `applyUpdate`         — sends SKIP_WAITING, activating the new SW and
 *                           triggering a page reload.
 */
const useServiceWorkerUpdate = () => {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  // Flips to true once the initial page-load window has elapsed. After that,
  // any newly detected update is classified as mid-session.
  const sessionEstablishedRef = useRef(false);

  // True when the update was detected after the session was established
  // (i.e. a deploy that landed while the user was already in the tab).
  // False for updates detected during the initial load — those are applied
  // silently so the user gets the new build without being interrupted.
  const [isMidSessionUpdate, setIsMidSessionUpdate] = useState(false);

  // Mark the session as established after the load window elapses.
  useEffect(() => {
    const timer = setTimeout(() => {
      sessionEstablishedRef.current = true;
    }, SESSION_WINDOW_MS);

    return () => clearTimeout(timer);
  }, []);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      registrationRef.current = registration;

      // If a worker is already waiting at registration time, this is a
      // load-time update (the tab was opened after a deploy while the old
      // SW was cached). isMidSessionUpdate stays false so it is applied
      // silently.

      // Classify future detected updates at *detection* time (updatefound),
      // not at install-complete time (needsRefresh). updatefound fires quickly
      // (SW script fetch + byte compare, decoupled from precache bundle size),
      // so sessionEstablishedRef reliably distinguishes fresh-open from
      // mid-session deploys regardless of how long precaching takes.
      registration.addEventListener('updatefound', () => {
        setIsMidSessionUpdate(sessionEstablishedRef.current);
      });

      // Periodic background poll as a fallback for tabs that miss
      // visibility/focus events (e.g. pinned background tabs).
      setInterval(() => {
        registration.update().catch(() => {
          // Transient network error or stale SW registration — swallow and
          // let the next interval retry.
        });
      }, POLLING_INTERVAL_MS);
    }
  });

  const checkForUpdate = useCallback(async () => {
    const registration = registrationRef.current;

    if (!registration) return;
    try {
      await registration.update();
    } catch {
      // Transient network error — swallow and let the next check retry.
    }
  }, []);

  const applyUpdate = useCallback(() => {
    updateServiceWorker(true).catch(() => {
      // If the waiting SW is already gone (activated by another tab or
      // cleaned up), SKIP_WAITING is never sent and controllerchange never
      // fires. Force a reload so the user always gets the latest build.
      window.location.reload();
    });
    // Fallback: if controllerchange doesn't fire within 3s (e.g. the waiting
    // SW was already activated elsewhere), unregister the SW before reloading
    // so the browser bypasses the SW cache and fetches fresh content from the
    // network instead of serving the stale cached page again.
    setTimeout(async () => {
      const reg = registrationRef.current;

      if (reg) await reg.unregister();
      window.location.reload();
    }, 3000);
  }, [updateServiceWorker]);

  return {
    needsRefresh: needRefresh,
    isMidSessionUpdate,
    checkForUpdate,
    applyUpdate
  };
};

export default useServiceWorkerUpdate;
